import { useState, useEffect, useRef } from "react";
import { createClient } from "@supabase/supabase-js";
import AudioEngine from "./audioEngine";

const SUPABASE_URL = "https://trtieaijkrdipiljyzuh.supabase.co";
const SUPABASE_KEY = "sb_publishable_eJKNPdo5XwSCH-X8Ttantw_feBJed2q";
const supabase = createClient(SUPABASE_URL, SUPABASE_KEY);

const SYS = "#38bdf8";
const SYS_BORDER = "rgba(56,189,248,0.25)";
const SYS_DIM = "rgba(56,189,248,0.12)";

// Global ranking uses 4 required categories
const GLOBAL_CATS = ["work","learning","health","creative"];
const GLOBAL_CAT_LABELS = {work:"Work",learning:"Learning",health:"Health",creative:"Creative"};
const GLOBAL_CAT_ICONS  = {work:"💼",learning:"📚",health:"💪",creative:"🎨"};

// Standard XP base for global ranking — same for everyone
const GLOBAL_STANDARD_XP = 100; // weekly XP per required category

// ─── Demon King Sword tier (kept in sync with task-tracker.jsx) ──────────────
function getSwordForLevel(level){
  if(level <= 10) return "/swords/sword_1.png";
  if(level <= 20) return "/swords/sword_2.png";
  if(level <= 30) return "/swords/sword_3.png";
  if(level <= 40) return "/swords/sword_4.png";
  if(level <= 50) return "/swords/sword_5.png";
  if(level <= 60) return "/swords/sword_6.png";
  if(level <= 70) return "/swords/sword_7.png";
  if(level <= 80) return "/swords/sword_8.png";
  return "/swords/sword_9.png";
}

// ─── Global XP calculation (standardized, read-time) ─────────────────────────
// Same proportional logic as personal but with fixed GLOBAL_STANDARD_XP base
function getEffectiveGoalAmtSimple(task, period){
  const g = task.goals?.[period];
  if(!g) return 0;
  if(task.metric === "time") return (parseInt(g.h||0)*60) + parseInt(g.m||0);
  return parseFloat(g.amount||0);
}

function globalXpForLog(task, log){
  const goal = getEffectiveGoalAmtSimple(task, "weekly") || getEffectiveGoalAmtSimple(task, "daily") * 7;
  if(!goal || goal <= 0) return GLOBAL_STANDARD_XP / 10;
  let value = 0;
  if(task.metric === "time") value = log.value?.totalMins || 0;
  else value = parseFloat(log.value?.amount || 0);
  if(value <= 0) return 0;
  return (value / goal) * GLOBAL_STANDARD_XP;
}

export function calcGlobalXP(tasks){
  // Only count tasks in required categories
  return tasks
    .filter(t => GLOBAL_CATS.includes(t.cat))
    .reduce((sum, t) =>
      sum + (t.logs||[]).reduce((s, log) => s + globalXpForLog(t, log), 0)
    , 0);
}

export function calcGlobalLevel(tasks){
  const xp = calcGlobalXP(tasks);
  // Fixed base for global: 4 categories × GLOBAL_STANDARD_XP
  const base = GLOBAL_CATS.length * GLOBAL_STANDARD_XP;
  let level=1, threshold=base, accumulated=0;
  while(xp >= accumulated+threshold){ accumulated+=threshold; level++; threshold=threshold*2; }
  return { level, xp:Math.floor(xp), base };
}

// eslint-disable-next-line no-unused-vars
function getTopCat(tasks){
  const xpByCat = {};
  GLOBAL_CATS.forEach(c => {
    xpByCat[c] = tasks
      .filter(t=>t.cat===c)
      .reduce((s,t)=>(t.logs||[]).reduce((ss,l)=>ss+globalXpForLog(t,l),s),0);
  });
  const top = Object.entries(xpByCat).sort((a,b)=>b[1]-a[1])[0];
  return top ? top[0] : null;
}

// ─── Premium Name Popup ───────────────────────────────────────────────────────
function PremiumNamePopup({ name, onClose }){
  return(
    <div style={{position:"fixed",inset:0,zIndex:10000,background:"rgba(0,0,0,0.85)",
      display:"flex",alignItems:"center",justifyContent:"center",padding:20}}
      onClick={onClose}>
      <div onClick={e=>e.stopPropagation()}
        style={{background:"#030912",border:"1px solid rgba(251,191,36,0.35)",
          borderRadius:16,padding:"28px 24px",maxWidth:340,width:"100%",
          boxShadow:"0 0 40px rgba(251,191,36,0.1)"}}>
        <div style={{fontFamily:"'Share Tech Mono',monospace",fontSize:10,
          color:"#fbbf24",letterSpacing:3,marginBottom:12}}>[ PREMIUM USERNAME ]</div>
        <div style={{fontSize:18,fontWeight:800,color:"#fbbf24",
          fontFamily:"'Share Tech Mono',monospace",marginBottom:16}}>{name}</div>
        <div style={{fontSize:13,color:"#94a3b8",lineHeight:1.7,marginBottom:20}}>
          Premium usernames are exclusive names from the ARISE universe —
          legendary hunter names, guild titles, and special monikers.
          They feature a golden shimmer effect and a special sword prefix visible to all players.
        </div>
        <div style={{fontSize:12,color:"#64748b",lineHeight:1.6,marginBottom:24,
          background:"rgba(251,191,36,0.05)",border:"1px solid rgba(251,191,36,0.15)",
          borderRadius:10,padding:"12px 14px"}}>
          To request this name:<br/><br/>
          1. Go to <strong style={{color:"#94a3b8"}}>Settings → Change Username</strong><br/>
          2. Request your current name as a premium name, or choose any name from the premium list<br/>
          3. Complete the payment as instructed by the admin<br/>
          4. The admin will review and update your name once confirmed
        </div>
        <button onClick={onClose}
          style={{width:"100%",background:"rgba(251,191,36,0.1)",
            border:"1px solid rgba(251,191,36,0.3)",borderRadius:10,
            padding:"12px",color:"#fbbf24",fontFamily:"'Share Tech Mono',monospace",
            fontSize:12,letterSpacing:2,cursor:"pointer"}}>
          UNDERSTOOD
        </button>
      </div>
    </div>
  );
}

// ─── Opt-in Screen ────────────────────────────────────────────────────────────
function OptInScreen({ playerName, onAgree, onDecline }){
  return(
    <div style={{maxWidth:560,width:"100%",margin:"0 auto",padding:"28px 16px 32px"}}>
      <div style={{marginBottom:24}}>
        <div style={{fontFamily:"'Share Tech Mono',monospace",fontSize:10,
          color:SYS,letterSpacing:3,marginBottom:6}}>[ GLOBAL RANKINGS ]</div>
        <div style={{fontSize:22,fontWeight:800,color:"#fff",
          fontFamily:"'Share Tech Mono',monospace",letterSpacing:2,marginBottom:4}}>
          JOIN THE RANKS?
        </div>
        <div style={{fontSize:13,color:"#94a3b8",lineHeight:1.6}}>
          See where <span style={{color:SYS,fontWeight:700}}>{playerName}</span> stands
          among all ARISE players worldwide.
        </div>
      </div>

      {/* What gets shared */}
      <div style={{background:"rgba(56,189,248,0.05)",border:`1px solid ${SYS_BORDER}`,
        borderRadius:12,padding:"16px",marginBottom:16}}>
        <div style={{fontFamily:"'Share Tech Mono',monospace",fontSize:10,
          color:SYS,letterSpacing:2,marginBottom:12}}>WHAT OTHERS WILL SEE</div>
        {[
          ["👤","Player Name","Your display name on the leaderboard"],
          ["⚡","Global Level","Calculated from your 4 required categories"],
          ["🏆","Global XP","Standardized score based on your logs"],
          ["📊","Top Category","Your strongest category (Work/Learn/Health/Creative)"],
        ].map(([icon,label,desc])=>(
          <div key={label} style={{display:"flex",gap:12,alignItems:"flex-start",marginBottom:10}}>
            <span style={{fontSize:16,flexShrink:0}}>{icon}</span>
            <div>
              <div style={{fontSize:12,fontWeight:700,color:"#c8d8e8"}}>{label}</div>
              <div style={{fontSize:11,color:"#64748b",marginTop:1}}>{desc}</div>
            </div>
          </div>
        ))}
      </div>

      {/* What's NOT shared */}
      <div style={{background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.06)",
        borderRadius:12,padding:"14px",marginBottom:24}}>
        <div style={{fontFamily:"'Share Tech Mono',monospace",fontSize:10,
          color:"#64748b",letterSpacing:2,marginBottom:8}}>WHAT STAYS PRIVATE</div>
        <div style={{fontSize:11,color:"#64748b",lineHeight:1.8}}>
          ✗ Your personal XP &amp; level settings<br/>
          ✗ Individual log details &amp; notes<br/>
          ✗ Your email address<br/>
          ✗ Targets not in the 4 required categories
        </div>
      </div>

      <div style={{display:"flex",flexDirection:"column",gap:10}}>
        <button onClick={onAgree}
          style={{padding:"14px",background:SYS_DIM,border:`1px solid ${SYS_BORDER}`,
            borderRadius:10,color:SYS,fontFamily:"'Share Tech Mono',monospace",
            fontSize:12,letterSpacing:2,cursor:"pointer",fontWeight:700}}>
          ✓ JOIN GLOBAL RANKING
        </button>
        <button onClick={onDecline}
          style={{padding:"12px",background:"none",border:"1px solid rgba(255,255,255,0.06)",
            borderRadius:10,color:"#64748b",fontFamily:"'Share Tech Mono',monospace",
            fontSize:11,letterSpacing:1,cursor:"pointer"}}>
          NOT NOW
        </button>
      </div>
    </div>
  );
}

// ─── Category Setup Screen ────────────────────────────────────────────────────
function CategorySetupScreen({ tasks, playerName, onComplete, onBack }){
  const missingCats = GLOBAL_CATS.filter(c => !tasks.some(t=>t.cat===c));
  const [step, setStep] = useState(0); // index into missingCats
  const [newTasks, setNewTasks] = useState([]);
  const [title, setTitle] = useState("");
  const [error, setError] = useState("");

  const currentCat = missingCats[step];

  function addCatTask(){
    const t = title.trim();
    if(!t){ setError("Enter a target name."); return; }
    const task = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2,7)}`,
      title: t,
      desc: "",
      cat: currentCat,
      status: "Not Started",
      progress: 0,
      logs: [],
      total: 0,
      metric: currentCat==="health"?"reps":currentCat==="learning"?"pages":"custom",
      customUnit: currentCat==="work"?"tasks":currentCat==="creative"?"sessions":"",
      goals: { weekly: { amount: "5" } },
      xp: 100,
      createdAt: new Date().toISOString(),
    };
    const updated = [...newTasks, task];
    setNewTasks(updated);
    setTitle("");
    setError("");
    if(step < missingCats.length - 1){
      setStep(step+1);
    } else {
      onComplete(updated);
    }
  }

  if(missingCats.length === 0){
    onComplete([]);
    return null;
  }

  const progress = step / missingCats.length;

  return(
    <div style={{maxWidth:560,width:"100%",margin:"0 auto",padding:"28px 16px 32px"}}>
      <button onClick={onBack}
        style={{background:"none",border:"none",color:"#64748b",cursor:"pointer",
          fontFamily:"monospace",fontSize:12,marginBottom:20,padding:0}}>
        ← Back
      </button>

      <div style={{marginBottom:24}}>
        <div style={{fontFamily:"'Share Tech Mono',monospace",fontSize:10,
          color:SYS,letterSpacing:3,marginBottom:6}}>[ GLOBAL RANKING SETUP ]</div>
        <div style={{fontSize:20,fontWeight:800,color:"#fff",
          fontFamily:"'Share Tech Mono',monospace",letterSpacing:2,marginBottom:8}}>
          CATEGORY SETUP
        </div>
        <div style={{fontSize:12,color:"#94a3b8",lineHeight:1.6,marginBottom:16}}>
          Global ranking requires at least one target in each of the 4 core categories.
          This ensures a fair, standardized comparison across all players.
        </div>
        {/* Progress bar */}
        <div style={{height:3,background:"rgba(255,255,255,0.05)",borderRadius:2,marginBottom:6}}>
          <div style={{height:"100%",background:SYS,borderRadius:2,
            width:`${progress*100}%`,transition:"width 0.4s"}}/>
        </div>
        <div style={{fontFamily:"monospace",fontSize:10,color:"#64748b"}}>
          {step} of {missingCats.length} categories set up
        </div>
      </div>

      {/* Already have */}
      {GLOBAL_CATS.filter(c=>tasks.some(t=>t.cat===c)).map(c=>(
        <div key={c} style={{display:"flex",alignItems:"center",gap:10,
          padding:"10px 14px",marginBottom:6,borderRadius:8,
          background:"rgba(74,222,128,0.06)",border:"1px solid rgba(74,222,128,0.15)"}}>
          <span style={{fontSize:16}}>{GLOBAL_CAT_ICONS[c]}</span>
          <span style={{fontFamily:"'Share Tech Mono',monospace",fontSize:11,color:"#4ade80"}}>
            {GLOBAL_CAT_LABELS[c]} ✓
          </span>
        </div>
      ))}

      {/* Current missing cat */}
      {currentCat&&(
        <div style={{marginTop:16,background:SYS_DIM,border:`1px solid ${SYS_BORDER}`,
          borderRadius:12,padding:"20px"}}>
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:16}}>
            <span style={{fontSize:24}}>{GLOBAL_CAT_ICONS[currentCat]}</span>
            <div>
              <div style={{fontFamily:"'Share Tech Mono',monospace",fontSize:12,
                color:SYS,letterSpacing:2}}>{GLOBAL_CAT_LABELS[currentCat].toUpperCase()}</div>
              <div style={{fontSize:11,color:"#64748b",marginTop:2}}>
                Add a target for this category
              </div>
            </div>
          </div>
          <input
            value={title}
            onChange={e=>{setTitle(e.target.value);setError("");}}
            placeholder={`e.g. ${currentCat==="work"?"Daily Tasks":currentCat==="learning"?"Reading":currentCat==="health"?"Workout":"Creative Project"}`}
            className="field"
            style={{marginBottom:error?8:12}}
            onKeyDown={e=>e.key==="Enter"&&addCatTask()}
          />
          {error&&<div style={{fontSize:11,color:"#ef4444",marginBottom:10}}>{error}</div>}
          <button onClick={addCatTask}
            style={{width:"100%",padding:"12px",background:SYS,border:"none",
              borderRadius:8,color:"#000",fontFamily:"'Share Tech Mono',monospace",
              fontSize:12,letterSpacing:2,cursor:"pointer",fontWeight:700}}>
            {step < missingCats.length-1 ? `ADD & NEXT →` : `COMPLETE SETUP ✓`}
          </button>
        </div>
      )}

      {/* Remaining missing (greyed) */}
      {missingCats.slice(step+1).map(c=>(
        <div key={c} style={{display:"flex",alignItems:"center",gap:10,
          padding:"10px 14px",marginTop:6,borderRadius:8,
          background:"rgba(255,255,255,0.02)",border:"1px solid rgba(255,255,255,0.05)"}}>
          <span style={{fontSize:16,opacity:0.4}}>{GLOBAL_CAT_ICONS[c]}</span>
          <span style={{fontFamily:"'Share Tech Mono',monospace",fontSize:11,color:"#334"}}>
            {GLOBAL_CAT_LABELS[c]}
          </span>
        </div>
      ))}
    </div>
  );
}

// ─── Leaderboard Row ──────────────────────────────────────────────────────────
const ROW_HEIGHT = 64;

function LeaderboardRow({ entry, rank, isMe, onPremiumClick, onMeClick }){
  const isPremium = entry.is_premium;
  const topCatIcon = GLOBAL_CAT_ICONS[entry.topCat] || "⚡";
  const topCatLabel = GLOBAL_CAT_LABELS[entry.topCat] || "";

  // Dynamic font size for name to fit box
  const nameLen = (entry.player_name||"").length;
  const nameFontSize = nameLen > 18 ? 10 : nameLen > 14 ? 12 : nameLen > 10 ? 14 : 16;

  const medal = rank===1?"🥇":rank===2?"🥈":rank===3?"🥉":null;

  return(
    <div
      onClick={isMe ? onMeClick : (isPremium ? ()=>onPremiumClick(entry.player_name) : undefined)}
      style={{
        display:"flex",alignItems:"stretch",
        height:ROW_HEIGHT,marginBottom:5,borderRadius:10,overflow:"hidden",
        background:isMe?"rgba(56,189,248,0.09)":isPremium?"rgba(251,191,36,0.04)":"rgba(255,255,255,0.02)",
        border:isMe?"1px solid rgba(56,189,248,0.35)":isPremium?"1px solid rgba(251,191,36,0.25)":"1px solid rgba(255,255,255,0.05)",
        boxShadow:isMe?"0 0 18px rgba(56,189,248,0.12), inset 0 0 20px rgba(56,189,248,0.04)":isPremium?"0 0 12px rgba(251,191,36,0.08)":"none",
        backdropFilter:isMe?"blur(8px)":"none",
        cursor:isMe?"pointer":isPremium?"pointer":"default",
        transition:"all 0.2s",
      }}>

      {/* Rank — full height left column */}
      <div style={{
        width:44,flexShrink:0,
        display:"flex",alignItems:"center",justifyContent:"center",
        borderRight:isMe?"1px solid rgba(56,189,248,0.2)":"1px solid rgba(255,255,255,0.04)",
        background:isMe?"rgba(56,189,248,0.06)":"rgba(255,255,255,0.01)",
      }}>
        {medal
          ? <span style={{fontSize:20}}>{medal}</span>
          : <span style={{fontFamily:"'Share Tech Mono',monospace",fontSize:11,
              color:isMe?SYS:"#64748b",fontWeight:700}}>#{rank}</span>
        }
      </div>

      {/* Content */}
      <div style={{flex:1,display:"flex",flexDirection:"column",
        justifyContent:"center",padding:"0 12px",minWidth:0}}>

        {/* Name row — 70% height */}
        <div style={{display:"flex",alignItems:"center",gap:6,
          height:`${ROW_HEIGHT*0.7}px`,minWidth:0}}>
          <div style={{position:"relative",flexShrink:0,width:nameFontSize*1.1,height:nameFontSize*1.1,display:"flex",alignItems:"center",justifyContent:"center"}}>
            <img src={getSwordForLevel(entry.level||1)} alt=""
              style={{width:"100%",height:"100%",objectFit:"contain",
                filter:isPremium?"drop-shadow(0 0 4px rgba(251,191,36,0.8))":"drop-shadow(0 0 3px rgba(148,163,184,0.5))",
                mixBlendMode:"screen"}}/>
          </div>
          {isPremium ? (
            <span style={{
              fontFamily:"'Share Tech Mono',monospace",
              fontSize:nameFontSize,
              fontWeight:700,
              position:"relative",
              display:"inline-block",
              overflow:"hidden",
              textOverflow:"ellipsis",
              whiteSpace:"nowrap",
              background:"linear-gradient(90deg,#92400e,#fbbf24,#fef3c7,#fbbf24,#d97706,#fbbf24,#92400e)",
              backgroundSize:"200% auto",
              WebkitBackgroundClip:"text",
              WebkitTextFillColor:"transparent",
              backgroundClip:"text",
              animation:"goldShimmer 3s linear infinite",
              textShadow:"none",
            }}>
              {entry.player_name}
            </span>
          ) : (
            <span style={{
              fontFamily:"'Share Tech Mono',monospace",
              fontSize:nameFontSize,
              fontWeight:700,
              color:isMe?SYS:"#c8d8e8",
              textShadow:isMe?`0 0 10px ${SYS}`:"none",
              overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",
              letterSpacing:isMe?1:0,
            }}>
              {entry.player_name}
            </span>
          )}
          {isMe&&(
            <span style={{fontFamily:"monospace",fontSize:8,color:SYS,
              background:"rgba(56,189,248,0.15)",padding:"1px 5px",
              borderRadius:4,letterSpacing:1,flexShrink:0,border:`1px solid ${SYS_BORDER}`}}>
              YOU · TAP
            </span>
          )}
        </div>

        {/* Level + category row — 30% height */}
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",
          height:`${ROW_HEIGHT*0.3}px`,paddingRight:4}}>
          <span style={{fontFamily:"monospace",fontSize:10,
            color:isMe?"#94a3b8":"#64748b"}}>
            Lv.{entry.level}
          </span>
          {entry.topCat&&(
            <span style={{display:"flex",alignItems:"center",gap:3,
              fontSize:10,color:"#4ade80",fontFamily:"monospace"}}>
              {topCatIcon} {topCatLabel} <span style={{fontSize:12}}>↑</span>
            </span>
          )}
        </div>
      </div>

      {/* XP */}
      <div style={{width:52,flexShrink:0,display:"flex",alignItems:"center",
        justifyContent:"center",paddingRight:10}}>
        <div style={{textAlign:"right"}}>
          <div style={{fontFamily:"'Share Tech Mono',monospace",fontSize:11,
            color:isMe?SYS:"#64748b",fontWeight:700}}>{entry.xp}</div>
          <div style={{fontSize:8,color:"#334",fontFamily:"monospace",letterSpacing:1}}>XP</div>
        </div>
      </div>
    </div>
  );
}

// ─── My Global Stats Panel ────────────────────────────────────────────────────
function MyStatsPanel({ tasks, onClose }){
  const globalTasks = tasks.filter(t => GLOBAL_CATS.includes(t.cat));
  const { level, xp, base } = calcGlobalLevel(tasks);

  // XP needed for next level
  let accumulated = 0, threshold = base;
  while(xp >= accumulated + threshold){ accumulated += threshold; threshold *= 2; }
  const xpIntoLevel = xp - Math.floor(accumulated);
  const xpForNext = Math.round(threshold);
  const pct = Math.round((xpIntoLevel / xpForNext) * 100);

  const totalXPAllCats = Math.floor(calcGlobalXP(tasks));

  return(
    <div style={{position:"fixed",inset:0,zIndex:9200,background:"rgba(0,0,0,0.92)",
      display:"flex",alignItems:"flex-end",justifyContent:"center",paddingBottom:90}}
      onClick={onClose}>
      <div onClick={e=>e.stopPropagation()}
        style={{width:"100%",maxWidth:560,background:"#030912",
          border:"1px solid rgba(56,189,248,0.2)",borderRadius:"18px 18px 0 0",
          maxHeight:"calc(85vh - 90px)",display:"flex",flexDirection:"column",
          animation:"slideUp 0.3s cubic-bezier(0.34,1.56,0.64,1)"}}>

        {/* Header */}
        <div style={{padding:"20px 20px 12px",borderBottom:"1px solid rgba(56,189,248,0.1)",
          display:"flex",justifyContent:"space-between",alignItems:"center",flexShrink:0}}>
          <div>
            <div style={{fontFamily:"'Share Tech Mono',monospace",fontSize:10,
              color:SYS,letterSpacing:3,marginBottom:4}}>[ MY GLOBAL STATS ]</div>
            <div style={{fontSize:18,fontWeight:800,color:"#fff",
              fontFamily:"'Share Tech Mono',monospace",letterSpacing:2}}>RANKING DETAILS</div>
          </div>
          <button onClick={onClose}
            style={{background:"none",border:"none",color:"#64748b",cursor:"pointer",fontSize:18}}>✕</button>
        </div>

        <div style={{overflowY:"auto",flex:1,padding:"16px 20px 32px"}}>

          {/* Global level card */}
          <div style={{background:SYS_DIM,border:`1px solid ${SYS_BORDER}`,
            borderRadius:12,padding:"16px",marginBottom:16}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:12}}>
              <div>
                <div style={{fontFamily:"'Share Tech Mono',monospace",fontSize:9,
                  color:SYS,letterSpacing:3,marginBottom:4}}>GLOBAL LEVEL</div>
                <div style={{fontFamily:"'Share Tech Mono',monospace",fontSize:36,
                  fontWeight:900,color:SYS,lineHeight:1}}>{level}</div>
              </div>
              <div style={{textAlign:"right"}}>
                <div style={{fontFamily:"monospace",fontSize:9,color:"#64748b",marginBottom:2}}>GLOBAL XP</div>
                <div style={{fontFamily:"'Share Tech Mono',monospace",fontSize:20,
                  fontWeight:800,color:"#fff"}}>{totalXPAllCats}</div>
                <div style={{fontFamily:"monospace",fontSize:9,color:"#64748b",marginTop:2}}>
                  base: {base} XP / level
                </div>
              </div>
            </div>
            <div style={{display:"flex",justifyContent:"space-between",
              fontFamily:"monospace",fontSize:10,color:"#64748b",marginBottom:6}}>
              <span>{Math.floor(xpIntoLevel)} / {xpForNext} XP → Level {level+1}</span>
              <span>{pct}%</span>
            </div>
            <div style={{height:4,background:"rgba(255,255,255,0.05)",borderRadius:2,overflow:"hidden"}}>
              <div style={{height:"100%",borderRadius:2,width:`${pct}%`,
                background:`linear-gradient(90deg,${SYS},#818cf8)`,transition:"width 0.5s"}}/>
            </div>
            <div style={{fontFamily:"monospace",fontSize:10,color:"#64748b",marginTop:8}}>
              Need <span style={{color:SYS,fontWeight:700}}>{xpForNext - Math.floor(xpIntoLevel)} more XP</span> to reach Level {level+1}
            </div>
          </div>

          {/* Per-category breakdown */}
          <div style={{fontFamily:"'Share Tech Mono',monospace",fontSize:10,
            color:SYS,letterSpacing:2,marginBottom:12}}>[ CATEGORY BREAKDOWN ]</div>

          {GLOBAL_CATS.map(cat=>{
            const catTasks = globalTasks.filter(t=>t.cat===cat);
            const catXP = Math.floor(catTasks.reduce((s,t)=>
              s+(t.logs||[]).reduce((ss,l)=>ss+globalXpForLog(t,l),0),0));
            return(
              <div key={cat} style={{background:"rgba(255,255,255,0.02)",
                border:"1px solid rgba(255,255,255,0.06)",borderRadius:12,
                marginBottom:10,overflow:"hidden"}}>
                {/* Category header */}
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",
                  padding:"10px 14px",borderBottom:catTasks.length?"1px solid rgba(255,255,255,0.04)":"none"}}>
                  <div style={{display:"flex",alignItems:"center",gap:8}}>
                    <span style={{fontSize:16}}>{GLOBAL_CAT_ICONS[cat]}</span>
                    <span style={{fontFamily:"'Share Tech Mono',monospace",fontSize:12,
                      fontWeight:700,color:"#c8d8e8"}}>{GLOBAL_CAT_LABELS[cat]}</span>
                    {catTasks.length===0&&(
                      <span style={{fontFamily:"monospace",fontSize:9,color:"#ef4444",
                        background:"rgba(239,68,68,0.1)",padding:"1px 6px",borderRadius:4}}>NO TARGET</span>
                    )}
                  </div>
                  <span style={{fontFamily:"'Share Tech Mono',monospace",fontSize:12,
                    fontWeight:700,color:SYS}}>{catXP} XP</span>
                </div>

                {/* Per-target rows */}
                {catTasks.map(t=>{
                  const taskXP = Math.floor((t.logs||[]).reduce((s,l)=>s+globalXpForLog(t,l),0));
                  const goal = getEffectiveGoalAmtSimple(t,"weekly")||getEffectiveGoalAmtSimple(t,"daily")*7;
                  const perLog = goal>0 ? Math.round((GLOBAL_STANDARD_XP/goal)*100)/100 : GLOBAL_STANDARD_XP/10;
                  const logs = t.logs||[];
                  return(
                    <div key={t.id} style={{padding:"10px 14px 10px 36px",
                      borderBottom:"1px solid rgba(255,255,255,0.03)"}}>
                      <div style={{display:"flex",justifyContent:"space-between",
                        alignItems:"flex-start",marginBottom:6}}>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{fontSize:12,fontWeight:700,color:"#c8d8e8",
                            overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{t.title}</div>
                          <div style={{fontFamily:"monospace",fontSize:10,color:"#64748b",marginTop:2}}>
                            {perLog} XP/unit · weekly goal: {goal||"none"}
                          </div>
                        </div>
                        <div style={{textAlign:"right",flexShrink:0,marginLeft:12}}>
                          <div style={{fontFamily:"'Share Tech Mono',monospace",fontSize:12,
                            fontWeight:700,color:SYS}}>{taskXP} XP</div>
                          <div style={{fontFamily:"monospace",fontSize:9,color:"#64748b"}}>
                            {logs.length} log{logs.length!==1?"s":""}
                          </div>
                        </div>
                      </div>

                      {/* Last 5 logs */}
                      {logs.length>0&&(
                        <div style={{display:"flex",flexDirection:"column",gap:3,marginTop:4}}>
                          {logs.slice(0,5).map((log,i)=>{
                            const logXP = Math.round(globalXpForLog(t,log)*100)/100;
                            const val = t.metric==="time"
                              ? `${Math.floor((log.value?.totalMins||0)/60)||""}${Math.floor((log.value?.totalMins||0)/60)?"h ":""}${(log.value?.totalMins||0)%60}m`
                              : `${log.value?.amount||0} ${t.customUnit||t.metric}`;
                            const date = new Date(log.date).toLocaleDateString("en-US",{month:"short",day:"numeric"});
                            return(
                              <div key={i} style={{display:"flex",justifyContent:"space-between",
                                alignItems:"center",padding:"3px 8px",
                                background:"rgba(56,189,248,0.04)",borderRadius:6}}>
                                <span style={{fontFamily:"monospace",fontSize:9,color:"#64748b"}}>
                                  {date} · {val}
                                </span>
                                <span style={{fontFamily:"'Share Tech Mono',monospace",fontSize:9,
                                  color:SYS,fontWeight:700}}>+{logXP} XP</span>
                              </div>
                            );
                          })}
                          {logs.length>5&&(
                            <div style={{fontFamily:"monospace",fontSize:9,color:"#64748b",
                              textAlign:"center",marginTop:2}}>
                              +{logs.length-5} more logs
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}


export default function GlobalRanking({
  user, tasks, playerName, isPremium,
  onTasksAdded,   // callback(newTasks) to add tasks to main app
  onOptInSaved,   // callback() when opt-in saved
  globalOptedIn,  // boolean from profiles
}){
  const [phase, setPhase] = useState(
    globalOptedIn ? "leaderboard" : "optin"
  ); // optin | setup | leaderboard
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(false);
  const [premiumPopup, setPremiumPopup] = useState(null);
  const [showMyStats, setShowMyStats] = useState(false);
  const [leaderboardCacheTime, setLeaderboardCacheTime] = useState(null);
  const [isStale, setIsStale] = useState(false);
  const myRowRef = useRef(null);

  // Load cached leaderboard immediately on mount
  useEffect(()=>{
    try{
      const cached = localStorage.getItem("arise_leaderboard");
      const cachedTime = localStorage.getItem("arise_leaderboard_time");
      if(cached){
        setLeaderboard(JSON.parse(cached));
        setLeaderboardCacheTime(cachedTime ? parseInt(cachedTime) : null);
        if(!navigator.onLine) setIsStale(true);
      }
    }catch{}
  },[]);

  useEffect(()=>{
    if(phase==="leaderboard") fetchLeaderboard();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[phase]);

  // Auto-scroll to current user after load
  useEffect(()=>{
    if(leaderboard.length>0 && myRowRef.current){
      setTimeout(()=>{
        myRowRef.current?.scrollIntoView({block:"center",behavior:"smooth"});
      },300);
    }
  },[leaderboard]);

  async function fetchLeaderboard(){
    if(!navigator.onLine){
      // Already showing cached data from useEffect — just mark as stale
      setIsStale(true);
      setLoading(false);
      return;
    }
    setLoading(true);
    setIsStale(false);
    const {data:profiles}=await supabase
      .from("profiles")
      .select("id,player_name,global_xp,global_level,global_top_cat")
      .eq("global_ranking_opted_in",true)
      .order("global_xp",{ascending:false})
      .limit(500);
    if(!profiles){ setLoading(false); return; }

    const {data:premNames}=await supabase
      .from("premium_usernames")
      .select("name,granted_to");
    const premSet=new Set((premNames||[]).filter(p=>p.granted_to).map(p=>p.granted_to));

    const rows=profiles.map(p=>({
      id:p.id,
      player_name:p.player_name,
      xp:p.global_xp||0,
      level:p.global_level||1,
      topCat:p.global_top_cat||null,
      is_premium:premSet.has(p.id),
    }));

    setLeaderboard(rows);
    setLeaderboardCacheTime(Date.now());

    // Cache for offline use
    try{
      localStorage.setItem("arise_leaderboard", JSON.stringify(rows));
      localStorage.setItem("arise_leaderboard_time", String(Date.now()));
    }catch{}

    setLoading(false);
  }

  async function handleOptIn(){
    // Check if all 4 categories have at least one task
    const missingCats = GLOBAL_CATS.filter(c=>!tasks.some(t=>t.cat===c));
    if(missingCats.length > 0){
      setPhase("setup");
    } else {
      await saveOptIn();
    }
  }

  async function saveOptIn(){
    await supabase.from("profiles")
      .update({global_ranking_opted_in:true})
      .eq("id",user.id);
    onOptInSaved();
    setPhase("leaderboard");
  }

  async function handleSetupComplete(newTasks){
    if(newTasks.length > 0) onTasksAdded(newTasks);
    await saveOptIn();
  }

  // Calculate visible count to determine centering logic
  const myIndex = leaderboard.findIndex(p=>p.id===user?.id);
  const total = leaderboard.length;

  // Centering logic: show user in center unless in top/bottom half of visible area
  function getRef(i){
    if(i === myIndex) return myRowRef;
    return null;
  }

  // ── Render ──
  if(phase==="optin"){
    return(
      <OptInScreen
        playerName={playerName}
        onAgree={handleOptIn}
        onDecline={()=>{}} // stays on optin screen, user can navigate away
      />
    );
  }

  if(phase==="setup"){
    return(
      <CategorySetupScreen
        tasks={tasks}
        playerName={playerName}
        onComplete={handleSetupComplete}
        onBack={()=>setPhase("optin")}
      />
    );
  }

  // Leaderboard
  return(
    <div style={{maxWidth:560,width:"100%",margin:"0 auto",padding:"24px 16px 32px"}}>
      <style>{`
        @keyframes goldShimmer{0%{background-position:200% center}100%{background-position:-200% center}}
      `}</style>
      {premiumPopup&&(
        <PremiumNamePopup name={premiumPopup} onClose={()=>{AudioEngine.close();setPremiumPopup(null);}}/>
      )}
      {showMyStats&&(
        <MyStatsPanel tasks={tasks} onClose={()=>{AudioEngine.close();setShowMyStats(false);}}/>
      )}

      <div style={{marginBottom:20,display:"flex",justifyContent:"space-between",alignItems:"flex-end"}}>
        <div>
          <div style={{fontFamily:"'Share Tech Mono',monospace",fontSize:10,
            color:SYS,letterSpacing:3,marginBottom:4}}>[ GLOBAL RANKINGS ]</div>
          <div style={{fontSize:22,fontWeight:800,color:"#fff",
            fontFamily:"'Share Tech Mono',monospace",letterSpacing:2}}>LEADERBOARD</div>
          {isStale&&leaderboardCacheTime&&(
            <div style={{fontFamily:"monospace",fontSize:10,color:"#f59e0b",marginTop:4,
              display:"flex",alignItems:"center",gap:4}}>
              <span>⚠️</span>
              <span>Offline · last updated {(()=>{
                const mins = Math.floor((Date.now()-leaderboardCacheTime)/60000);
                if(mins<1) return "just now";
                if(mins<60) return `${mins}m ago`;
                const hrs = Math.floor(mins/60);
                if(hrs<24) return `${hrs}h ago`;
                return `${Math.floor(hrs/24)}d ago`;
              })()}</span>
            </div>
          )}
        </div>
        {myIndex>=0&&(
          <div style={{fontFamily:"monospace",fontSize:11,color:"#64748b",textAlign:"right"}}>
            <div>Your rank: <span style={{color:SYS,fontWeight:700}}>#{myIndex+1}</span>
            <span style={{color:"#64748b"}}> / {total}</span></div>
            {isStale&&<div style={{fontSize:9,color:"#f59e0b",marginTop:2}}>CACHED</div>}
          </div>
        )}
      </div>

      {loading?(
        <div style={{textAlign:"center",padding:"60px 20px",
          fontFamily:"monospace",fontSize:12,color:"#64748b"}}>
          <div style={{fontSize:24,marginBottom:12}}>🏆</div>
          Loading rankings...
        </div>
      ):(
        leaderboard.length===0?(
          <div style={{textAlign:"center",padding:"60px 20px",
            background:SYS_DIM,border:`1px solid ${SYS_BORDER}`,borderRadius:14}}>
            <div style={{fontSize:32,marginBottom:12}}>🏆</div>
            <div style={{fontFamily:"'Share Tech Mono',monospace",fontSize:12,
              color:SYS,letterSpacing:2,marginBottom:8}}>NO PLAYERS YET</div>
            <div style={{fontSize:12,color:"#64748b"}}>Be the first on the leaderboard!</div>
          </div>
        ):(
          leaderboard.map((entry,i)=>(
            <div key={entry.id} ref={getRef(i)}>
              <LeaderboardRow
                entry={entry}
                rank={i+1}
                isMe={entry.id===user?.id}
                onPremiumClick={(name)=>{AudioEngine.open();setPremiumPopup(name);}}
                onMeClick={entry.id===user?.id ? ()=>{AudioEngine.open();setShowMyStats(true);} : null}
              />
            </div>
          ))
        )
      )}
    </div>
  );
}

// ─── Dual Level Up Screen (personal + global) ─────────────────────────────────
export function DualLevelUpScreen({ personal, global: globalLvl, onContinue }){
  // personal: {from, to} | null
  // globalLvl: {from, to} | null
  if(!personal && !globalLvl) return null;

  if(personal && globalLvl){
    // Both — two boxes stacked around center
    return(
      <div style={{position:"fixed",inset:0,zIndex:9999,background:"rgba(2,6,14,0.97)",
        display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:16}}>
        <style>{`
          @keyframes lineReveal{
            0%{clip-path:inset(0 50% 0 50%);}
            22%{clip-path:inset(0 calc(50% - 1.5px) 0 calc(50% - 1.5px));}
            100%{clip-path:inset(0 0 0 0);}
          }
          @keyframes lineGlow{
            0%{opacity:0;}
            15%{opacity:1;}
            45%{opacity:1;}
            100%{opacity:0;}
          }
        `}</style>

        {/* Personal — above center */}
        <div style={{position:"relative",width:"85%",maxWidth:320}}>
          <div style={{position:"absolute",top:0,left:"50%",width:3,height:"100%",
            background:SYS,boxShadow:`0 0 14px ${SYS}, 0 0 28px ${SYS}`,
            transform:"translateX(-50%)",borderRadius:2,zIndex:1,
            animation:"lineGlow 0.5s ease forwards"}}/>
          <div style={{background:"rgba(56,189,248,0.07)",border:"1px solid rgba(56,189,248,0.25)",
            borderRadius:16,padding:"24px 32px",textAlign:"center",width:"100%",position:"relative",zIndex:2,
            boxShadow:"0 0 40px rgba(56,189,248,0.15)",
            animation:"lineReveal 0.5s cubic-bezier(0.4,0,0.2,1) forwards"}}>
            <div style={{fontFamily:"'Share Tech Mono',monospace",fontSize:9,letterSpacing:3,
              color:SYS,marginBottom:8}}>[ PERSONAL ]</div>
            <div style={{fontSize:32,fontWeight:900,color:"#fff",
              textShadow:"0 0 20px rgba(56,189,248,0.8)",
              fontFamily:"'Share Tech Mono',monospace",marginBottom:8}}>
              LEVEL UP
            </div>
            <div style={{fontSize:18,fontWeight:700,color:SYS,
              fontFamily:"'Share Tech Mono',monospace",letterSpacing:2,marginBottom:16}}>
              Lv.{personal.from} → Lv.{personal.to}
            </div>
            <button onClick={onContinue}
              style={{background:"rgba(56,189,248,0.12)",border:"1px solid rgba(56,189,248,0.4)",
                borderRadius:8,padding:"10px 24px",color:SYS,
                fontFamily:"'Share Tech Mono',monospace",fontSize:11,letterSpacing:2,cursor:"pointer"}}>
              ✓ CONTINUE
            </button>
          </div>
        </div>

        {/* Global — below center */}
        <div style={{position:"relative",width:"85%",maxWidth:320}}>
          <div style={{position:"absolute",top:0,left:"50%",width:3,height:"100%",
            background:"#fbbf24",boxShadow:"0 0 14px #fbbf24, 0 0 28px #fbbf24",
            transform:"translateX(-50%)",borderRadius:2,zIndex:1,
            animation:"lineGlow 0.5s ease 0.15s both"}}/>
          <div style={{background:"rgba(251,191,36,0.06)",border:"1px solid rgba(251,191,36,0.25)",
            borderRadius:16,padding:"24px 32px",textAlign:"center",width:"100%",position:"relative",zIndex:2,
            boxShadow:"0 0 40px rgba(251,191,36,0.1)",
            animation:"lineReveal 0.5s cubic-bezier(0.4,0,0.2,1) 0.15s both"}}>
            <div style={{fontFamily:"'Share Tech Mono',monospace",fontSize:9,letterSpacing:3,
              color:"#fbbf24",marginBottom:8}}>[ GLOBAL RANKING ]</div>
            <div style={{fontSize:32,fontWeight:900,color:"#fff",
              textShadow:"0 0 20px rgba(251,191,36,0.6)",
              fontFamily:"'Share Tech Mono',monospace",marginBottom:8}}>
              🏆 LEVEL UP
            </div>
            <div style={{fontSize:18,fontWeight:700,color:"#fbbf24",
              fontFamily:"'Share Tech Mono',monospace",letterSpacing:2,marginBottom:16}}>
              Lv.{globalLvl.from} → Lv.{globalLvl.to}
            </div>
            <button onClick={onContinue}
              style={{background:"rgba(251,191,36,0.1)",border:"1px solid rgba(251,191,36,0.3)",
                borderRadius:8,padding:"10px 24px",color:"#fbbf24",
                fontFamily:"'Share Tech Mono',monospace",fontSize:11,letterSpacing:2,cursor:"pointer"}}>
              ✓ CONTINUE
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Only global
  if(globalLvl){
    return(
      <div style={{position:"fixed",inset:0,zIndex:9999,background:"rgba(2,6,14,0.97)",
        display:"flex",alignItems:"center",justifyContent:"center"}}>
        <style>{`
          @keyframes lineReveal{
            0%{clip-path:inset(0 50% 0 50%);}
            22%{clip-path:inset(0 calc(50% - 1.5px) 0 calc(50% - 1.5px));}
            100%{clip-path:inset(0 0 0 0);}
          }
          @keyframes lineGlow{
            0%{opacity:0;}
            15%{opacity:1;}
            45%{opacity:1;}
            100%{opacity:0;}
          }
        `}</style>
        <div style={{position:"relative",maxWidth:340,width:"90%"}}>
          <div style={{position:"absolute",top:0,left:"50%",width:3,height:"100%",
            background:"#fbbf24",boxShadow:"0 0 14px #fbbf24, 0 0 28px #fbbf24",
            transform:"translateX(-50%)",borderRadius:2,zIndex:1,
            animation:"lineGlow 0.6s ease forwards"}}/>
          <div style={{background:"rgba(251,191,36,0.06)",border:"1px solid rgba(251,191,36,0.25)",
            borderRadius:20,padding:"48px 40px",textAlign:"center",width:"100%",position:"relative",zIndex:2,
            boxShadow:"0 0 60px rgba(251,191,36,0.15)",
            animation:"lineReveal 0.6s cubic-bezier(0.4,0,0.2,1) forwards"}}>
            <div style={{fontFamily:"'Share Tech Mono',monospace",fontSize:11,letterSpacing:4,
              color:"#fbbf24",marginBottom:24}}>[ GLOBAL RANKING ]</div>
            <div style={{fontSize:48,fontWeight:900,color:"#fff",
              textShadow:"0 0 30px rgba(251,191,36,0.6)",
              fontFamily:"'Share Tech Mono',monospace",marginBottom:16}}>
              🏆 LEVEL UP
            </div>
            <div style={{fontSize:22,fontWeight:700,color:"#fbbf24",
              fontFamily:"'Share Tech Mono',monospace",letterSpacing:2,marginBottom:40}}>
              Lv.{globalLvl.from} → Lv.{globalLvl.to}
            </div>
            <button onClick={onContinue}
              style={{background:"rgba(251,191,36,0.1)",border:"1px solid rgba(251,191,36,0.3)",
                borderRadius:10,padding:"14px 32px",color:"#fbbf24",
                fontFamily:"'Share Tech Mono',monospace",fontSize:13,letterSpacing:2,
                cursor:"pointer",display:"flex",alignItems:"center",gap:10,margin:"0 auto"}}>
              ✓ CONTINUE
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Only personal (fallback — normally handled by LevelUpScreen)
  return null;
}
